import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, CheckCircle2, AlertCircle, Info, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  compliant: { icon: CheckCircle2, hue: '#22d3ee', label: 'Compliant' },
  gap: { icon: AlertCircle, hue: '#f472b6', label: 'Gap' },
  not_yet_required: { icon: Info, hue: '#94a3b8', label: 'Not yet required' },
};

/**
 * NC CDP Compliance panel, the check-list the counselor (and audit trail)
 * needs to prove the platform satisfies § 115C-158.10.
 */
export default function ComplianceStatePanel({ studentId }) {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    const url = studentId
      ? `${API}/nc/cdp/compliance?student_id=${studentId}`
      : `${API}/nc/cdp/compliance`;
    axios.get(url, getAuthHeader())
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [getAuthHeader, studentId]);

  if (!data) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl border border-cyan-500/25 bg-[#0a1729]/70 p-6"
      style={{ boxShadow: '0 30px 80px -60px rgba(34,211,238,0.5)' }}
      data-testid="cdp-compliance-panel"
    >
      <div aria-hidden className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full blur-3xl opacity-25"
        style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.35), transparent 65%)' }} />

      <div className="relative">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-cyan-300" />
            <h2 className="font-outfit text-base font-semibold text-white">
              NC State Compliance · § 115C-158.10
            </h2>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-cyan-200">{data.compliance_score}%</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-300">
              {data.compliant_count} of {data.total_applicable} requirements
            </p>
          </div>
        </div>

        <div className="mt-4 h-1.5 w-full rounded-full bg-white/[0.06] overflow-hidden">
          <motion.div
            initial={{ width: 0 }} animate={{ width: `${data.compliance_score}%` }}
            transition={{ duration: 0.8 }}
            className="h-1.5 rounded-full bg-gradient-to-r from-cyan-400 via-sky-400 to-violet-500"
            style={{ boxShadow: '0 0 18px -6px rgba(34,211,238,0.7)' }}
          />
        </div>

        <div className="mt-5 grid gap-2 md:grid-cols-2">
          {data.checks.map((c) => {
            const s = STATUS[c.status] || STATUS.gap;
            const Icon = s.icon;
            return (
              <div key={c.key}
                data-testid={`compliance-check-${c.key}`}
                className="rounded-xl border p-3"
                style={{ borderColor: `${s.hue}33`, background: `${s.hue}0a` }}>
                <div className="flex items-start gap-2">
                  <Icon className="h-4 w-4 shrink-0 mt-0.5" style={{ color: s.hue }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-white">{c.label}</p>
                      <span className="text-[10px] uppercase tracking-wider text-slate-300">
                        Grades {c.grades}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-slate-300 leading-relaxed">{c.detail}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <span className="text-[10px] text-slate-300">{c.authority}</span>
                      {c.surfaces?.[0] && (
                        <Link to={c.surfaces[0]} className="text-[10px] text-cyan-300 hover:text-white inline-flex items-center gap-0.5">
                          Open <ExternalLink className="h-2.5 w-2.5" />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-4 text-[10px] text-slate-300 leading-relaxed">
          {data.citation}
        </p>
      </div>
    </motion.div>
  );
}
